<template>
  <div class="coverage-360">
    <!-- Columna izquierda: membresías del tutor; la elegida filtra servicios y coberturas. -->
    <v-card class="seccion-card memberships-list" data-test="memberships-list">
      <div class="list-head">
        <span class="list-title">Membresías · {{ memberships.length }}</span>
      </div>
      <SourceError360 v-if="hasError('memberships')" label="las membresías" @retry="$emit('retry', 'memberships')" />
      <v-skeleton-loader
        v-else-if="isLoading('memberships')"
        type="list-item-two-line@3"
        data-test="memberships-loading"
      />
      <div v-else-if="!memberships.length" class="empty-state" data-test="no-memberships-list">
        El usuario no tiene membresías.
      </div>
      <ul v-else class="item-list" aria-label="Membresías del tutor">
        <li v-for="m in sortedMemberships" :key="m.id">
          <button
            type="button"
            class="list-item"
            :class="{ 'is-selected': m.id === selectedMembershipId }"
            :aria-pressed="String(m.id === selectedMembershipId)"
            :data-test="`membership-item-${m.id}`"
            @click="selectedMembershipId = m.id"
          >
            <span class="item-name">
              <PetNameBadge :petName="m.name || '-'" :species="m.species" small showIcon />
              <v-tooltip v-if="overdueOf(m.petId)" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="error"
                    v-bind="attrs"
                    :aria-label="`${overdueOf(m.petId)} atrasada`"
                    :data-test="`membership-overdue-${m.id}`"
                    v-on="on"
                  >mdi-needle</v-icon>
                </template>
                <span>{{ overdueOf(m.petId) }} atrasada</span>
              </v-tooltip>
            </span>
            <span class="item-meta">
              <span class="item-plan">{{ m.planName }}</span>
              <MembershipStatusChip outlined :status="Number(m.status)" />
            </span>
            <span class="item-validity">{{ validityText(m) }}</span>
          </button>
        </li>
      </ul>
    </v-card>

    <div class="coverage-detail">
      <MembershipServiceUsage
        v-if="!hasError('memberships') && !isLoading('memberships')"
        :membership-id="selectedMembershipId"
        :token="token"
      />

      <PetHealth360Card
        v-if="selectedPetId"
        :health="selectedPetHealth"
        :loading="isLoading('petHealth')"
        :error="hasError('petHealth')"
        @retry="$emit('retry', 'petHealth')"
      />

      <div v-if="hasError('refunds') || hasError('usages')" class="source-errors" data-test="coverage-errors">
        <SourceError360 v-if="hasError('refunds')" label="los reembolsos" @retry="$emit('retry', 'refunds')" />
        <SourceError360
          v-if="hasError('usages')"
          label="los demás usos de cobertura"
          @retry="$emit('retry', 'usages')"
        />
      </div>
      <v-skeleton-loader v-if="coverageLoading" type="table" data-test="coverage-loading" />
      <CoverageUsageTable
        v-else
        :coverage-usage="coverageUsage"
        :membership-id="selectedMembershipId"
        :refunds-total="refundsTotal"
        :refunds-load-failed="refundsLoadFailed && !hasError('refunds')"
        :usage-load-failed="usageLoadFailed && !hasError('usages')"
      />
    </div>
  </div>
</template>

<script>
import MembershipServiceUsage from './MembershipServiceUsage.vue';
import CoverageUsageTable from './CoverageUsageTable.vue';
import SourceError360 from './SourceError360.vue';
import PetNameBadge from './PetNameBadge.vue';
import MembershipStatusChip from './MembershipStatusChip.vue';
import PetHealth360Card from './PetHealth360Card.vue';
import { ACTIVE_MEMBERSHIP } from '@/utils/constants';
import { petHealthOf, overdueSummary } from '@/utils/pet-health.utils';

const EMPTY_SOURCE = { loading: false, error: false };
const isActive = (membership) => Number(membership.status) === ACTIVE_MEMBERSHIP;

// Pestaña «Coberturas y servicios» de la 360 (113 US4): a la izquierda las membresías del tutor y,
// a la derecha, el uso de servicios, las vacunas y desparasitaciones de su mascota y el historial
// de uso de coberturas de la membresía elegida. Cada fuente que falla muestra su aviso con reintentar.
export default {
  name: 'Coverage360Tab',
  components: {
    MembershipServiceUsage,
    CoverageUsageTable,
    SourceError360,
    PetNameBadge,
    MembershipStatusChip,
    PetHealth360Card
  },
  props: {
    memberships: { type: Array, default: () => [] },
    token: { type: String, required: true },
    // Membresía elegida desde «Ver uso» (?membership=).
    initialMembershipId: { type: String, default: null },
    coverageUsage: { type: Array, default: () => [] },
    refundsTotal: { type: Number, default: 0 },
    refundsLoadFailed: { type: Boolean, default: false },
    usageLoadFailed: { type: Boolean, default: false },
    // Estado de carga de cada fuente: { memberships, refunds, usages, petHealth } con { loading, error }.
    loadState: { type: Object, default: () => ({}) },
    // Respuesta de GET /pets/360/health-status/:pgId; null mientras carga o si falló.
    petHealth: { type: Object, default: null }
  },
  emits: ['retry'],
  data() {
    return { selectedMembershipId: null };
  },
  computed: {
    // Mientras las dos fuentes cargan, la tabla vacía se leería como «sin usos».
    coverageLoading() {
      return this.isLoading('refunds') && this.isLoading('usages');
    },
    // Vigentes primero, luego históricas, conservando el orden de la 360.
    sortedMemberships() {
      return [...this.memberships.filter(isActive), ...this.memberships.filter((m) => !isActive(m))];
    },
    // Mascota de la membresía elegida; null si la membresía no tiene mascota.
    selectedPetId() {
      const membership = this.findMembership(this.selectedMembershipId);
      return (membership && membership.petId) || null;
    },
    selectedPetHealth() {
      return petHealthOf(this.petHealth, this.selectedPetId);
    }
  },
  watch: {
    // Conserva la elegida si sigue existiendo; si no, la de «Ver uso», la primera vigente o la primera.
    memberships: {
      immediate: true,
      handler() {
        if (this.findMembership(this.selectedMembershipId)) return;
        const preferred = this.findMembership(this.initialMembershipId) || this.sortedMemberships[0];
        this.selectedMembershipId = preferred ? preferred.id : null;
      }
    },
    // La pestaña queda montada (v-tab-item eager): un nuevo «Ver uso» cambia la membresía elegida.
    initialMembershipId(id) {
      if (this.findMembership(id)) this.selectedMembershipId = id;
    }
  },
  methods: {
    hasError(source) {
      return Boolean((this.loadState[source] || EMPTY_SOURCE).error);
    },
    isLoading(source) {
      return Boolean((this.loadState[source] || EMPTY_SOURCE).loading);
    },
    // 'Vacunas' / 'Desparasitación' / ambas si la mascota tiene algo atrasado; null si no.
    overdueOf(petId) {
      return petId ? overdueSummary(petHealthOf(this.petHealth, petId)) : null;
    },
    findMembership(id) {
      return id ? this.memberships.find((m) => m.id === id) : undefined;
    },
    validityText(m) {
      const start = m.startdate || '-';
      const end = m.expirationDate && m.expirationDate !== '-' ? m.expirationDate : null;
      return end ? `${start} – ${end}` : `Desde ${start}`;
    }
  }
};
</script>

<style scoped>
.coverage-360 {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 20px;
}
.memberships-list {
  flex: 1 1 260px;
  max-width: 320px;
}
.coverage-detail {
  flex: 4 1 600px;
  min-width: 0;
}
/* Cuando el detalle baja a otra fila, la lista ocupa todo el ancho. */
@media (max-width: 960px) {
  .memberships-list {
    max-width: none;
  }
}

.seccion-card {
  margin-bottom: 20px;
}
.list-head {
  padding: 15px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.list-title {
  font-size: 0.9375rem;
  font-weight: 600;
}
.item-list {
  list-style: none;
  padding: 6px 0;
  margin: 0;
}
.list-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  padding: 10px 20px;
  text-align: left;
  border-left: 3px solid transparent;
}
.list-item:hover {
  background: var(--pw-divider);
}
.list-item:focus-visible {
  outline: 2px solid var(--v-primary-base);
  outline-offset: -2px;
}
.list-item.is-selected {
  border-left-color: var(--v-primary-base);
  background: var(--pw-info-tint);
}
.item-name {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.item-meta {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 0.8125rem;
}
.item-validity {
  font-size: 0.75rem;
  color: var(--pw-text-muted);
  font-variant-numeric: tabular-nums;
}
.source-errors {
  margin-bottom: 8px;
}
.empty-state {
  padding: 24px 20px;
  color: var(--pw-text-muted);
}
</style>
